import React, { useEffect } from 'react'
import styled from '@emotion/styled'
import Modal from '@components/common/modal/Modal'
import MenuGenerationChat from './MenuGenerationChat'
import MenuGenerationSchedule from './MenuGenerationSchedule'
import MenuGenerationProfile from './MenuGenerationProfile'
import { useMenuGeneration } from './useMenuGeneration'
import { ClientSettings } from '@types'

interface MenuGenerationModalProps {
  clientSettings: ClientSettings
  userId: string
  hideModal: () => void
  onMenusCreated?: () => void
}

const MenuGenerationModal: React.FC<MenuGenerationModalProps> = ({
  clientSettings,
  userId,
  hideModal,
  onMenusCreated,
}) => {
  const clientSettingsId = clientSettings.id
  const clientName = clientSettings.name || clientSettings.client?.name
  const { universalMenuConfig } = clientSettings
  const {
    chatId,
    messages,
    scheduleJson,
    conceptMap,
    isLoading,
    loadingStatus,
    isAccepting,
    acceptResult,
    acceptProgress,
    isCreating,
    createResult,
    createProgress,
    profileMarkdown,
    profileLoading,
    error,
    createChat,
    sendMessage,
    stopGeneration,
    acceptSchedule,
    createDateMenus,
  } = useMenuGeneration()

  useEffect(() => {
    createChat(userId, clientSettingsId)
  }, [userId, clientSettingsId, createChat])

  useEffect(() => {
    return () => {
      stopGeneration()
    }
  }, [stopGeneration])

  return (
    <Modal
      title={`Generate Menu — ${clientName}`}
      width="90vw"
      hideModal={hideModal}
    >
      <ModalContent>
        <MainColumn>
          <ChatSection hasSchedule={!!scheduleJson}>
            {chatId ? (
              <MenuGenerationChat
                messages={messages}
                isLoading={isLoading}
                loadingStatus={loadingStatus}
                error={error}
                onSendMessage={sendMessage}
                onStopGeneration={stopGeneration}
              />
            ) : (
              <LoadingState>Initializing chat...</LoadingState>
            )}
          </ChatSection>
          {scheduleJson && (
            <ScheduleSection>
              <MenuGenerationSchedule
                schedule={scheduleJson}
                conceptMap={conceptMap}
                clientSettingsId={clientSettingsId}
                onAcceptSchedule={() => acceptSchedule(clientSettingsId)}
                isAccepting={isAccepting}
                acceptResult={acceptResult}
                acceptProgress={acceptProgress}
                acceptError={error}
                onCreateMenus={(activeMap) =>
                  createDateMenus(clientSettingsId, activeMap)
                }
                isCreating={isCreating}
                createResult={createResult}
                createProgress={createProgress}
                onCloseAll={onMenusCreated}
              />
            </ScheduleSection>
          )}
        </MainColumn>
        <ProfileColumn>
          <MenuGenerationProfile
            markdown={profileMarkdown}
            isLoading={profileLoading}
            universalMenuConfig={universalMenuConfig}
          />
        </ProfileColumn>
      </ModalContent>
    </Modal>
  )
}

const ModalContent = styled.div`
  display: flex;
  flex-direction: row;
  max-height: 80vh;
`

const MainColumn = styled.div`
  flex: 3;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const ProfileColumn = styled.div`
  flex: 2;
  border-left: 2px solid #e0e0e0;
  overflow-y: auto;
`

const ChatSection = styled.div<{ hasSchedule: boolean }>`
  flex: 1;
  min-height: 300px;
  max-height: ${(props) => (props.hasSchedule ? '40vh' : '60vh')};
  overflow-y: auto;
`

const ScheduleSection = styled.div`
  border-top: 2px solid #e0e0e0;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #888;
  font-size: 14px;
`

export default MenuGenerationModal
